import React, { useCallback, useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { Upload, Settings, FolderUp, X } from "lucide-react";
import { fileApi, folderApi } from "../services/api";
import { useLanguage } from "../contexts/LanguageContext";
import { useUpload } from "../contexts/UploadContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useModal } from "../hooks/useModal";
import toast from "react-hot-toast";
import { pdfjs } from "react-pdf";
import SourceLinksField, { MAX_SOURCE_LINKS, normalizeLinks, validateLinks } from "./SourceLinksField";

/* ─── Folder upload helpers ─── */

// OS/system files to silently ignore during folder upload
const IGNORED_FILES = new Set([
  ".ds_store", "thumbs.db", "desktop.ini", ".gitkeep", ".gitignore",
  ".thumbs", "icon\r", "ehthumbs.db", "ehthumbs_vista.db",
]);
const isIgnoredFile = (name) => IGNORED_FILES.has(name.toLowerCase()) || name.startsWith("._");

// Recursively read all files from a FileSystemDirectoryEntry
const readDirectoryEntriesRecursive = async (directoryEntry, basePath = "") => {
  const files = [];
  const reader = directoryEntry.createReader();

  const readBatch = () =>
    new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });

  // readEntries may return partial results — call until empty
  let batch;
  do {
    batch = await readBatch();
    for (const entry of batch) {
      const relativePath = basePath ? `${basePath}/${entry.name}` : entry.name;
      if (entry.isFile) {
        if (isIgnoredFile(entry.name)) continue;
        const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
        files.push({ file, relativePath, folderPath: basePath });
      } else if (entry.isDirectory) {
        const subFiles = await readDirectoryEntriesRecursive(entry, relativePath);
        files.push(...subFiles);
      }
    }
  } while (batch.length > 0);

  return files;
};

// Get PDF page count from a File object
const getPdfPageCount = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
  return pdf.numPages;
};

// Validate all files in a folder tree. Returns { valid: true } or { valid: false, errors: [...] }
const validateFolderFiles = async (fileEntries, supportedTypes, translate) => {
  const errors = [];
  const maxSize = 50 * 1024 * 1024; // 50MB
  const maxPdfPages = 100;

  for (const { file, relativePath } of fileEntries) {
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!supportedTypes.includes(ext)) {
      errors.push({ name: relativePath, reason: translate("fileTypeNotSupported", { extension: ext }) });
    } else if (file.size > maxSize) {
      errors.push({ name: relativePath, reason: translate("fileSizeTooLarge") });
    } else if (ext === ".pdf") {
      try {
        const pageCount = await getPdfPageCount(file);
        if (pageCount > maxPdfPages) {
          errors.push({ name: relativePath, reason: translate("pdfPageLimitExceeded", { totalPages: pageCount, maxPages: maxPdfPages }) });
        }
      } catch {
        errors.push({ name: relativePath, reason: translate("failedToLoadPDF") });
      }
    }
  }

  if (fileEntries.length === 0) {
    errors.push({ name: "", reason: translate("emptyFolder") });
  }

  return errors.length > 0 ? { valid: false, errors } : { valid: true };
};

// Build a folder tree structure from flat relative paths
// Returns: { name, children: { subName: { name, children, ... } } }
const buildFolderTree = (fileEntries) => {
  const tree = {};
  for (const { folderPath } of fileEntries) {
    if (!folderPath) continue;
    const parts = folderPath.split("/");
    let current = tree;
    for (const part of parts) {
      if (!current[part]) current[part] = {};
      current = current[part];
    }
  }
  return tree;
};

// Create folders top-down and return a map of folderPath → folderId
const createFolderTree = async (tree, collectionName, parentFolderId, prefix = "") => {
  const folderMap = {};
  for (const name of Object.keys(tree)) {
    const fullPath = prefix ? `${prefix}/${name}` : name;
    try {
      const result = await folderApi.createFolder(collectionName, name, parentFolderId);
      const newFolderId = result.id;
      folderMap[fullPath] = newFolderId;
      // Recurse into children
      const childMap = await createFolderTree(tree[name], collectionName, newFolderId, fullPath);
      Object.assign(folderMap, childMap);
    } catch (err) {
      // Mark this folder and all descendants as failed
      folderMap[fullPath] = null;
    }
  }
  return folderMap;
};

const FileUpload = forwardRef(({
  onFileUploaded,
  onUploadStarted,
  supportedTypes,
  collectionName,
  availableChats = [],
  folderId = null,
}, ref) => {
  const { translate } = useLanguage();
  // Use availableChats prop or fallback to empty array
  const chats = availableChats;
  const {
    uploading,
    setUploadingState,
    isProcessingMultiple,
    setIsProcessingMultiple,
    isDragOver,
    setIsDragOver,
    initFileQueue,
    updateFileInQueue,
  } = useUpload();

  // File dates state (for both campaigns and general information)
  const [showDateModal, setShowDateModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedChatIds, setSelectedChatIds] = useState([]);
  const [overrideFolderId, setOverrideFolderId] = useState(undefined); // undefined = use prop, null = root, string = folder
  // One array of links per pending file (index-aligned with pendingFiles).
  const [sourceUrls, setSourceUrls] = useState([]);
  const [sameDatesForAll, setSameDatesForAll] = useState(true);
  const [perFileStartDates, setPerFileStartDates] = useState([]);
  const [perFileEndDates, setPerFileEndDates] = useState([]);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // Folder upload state — modal is only for confirmation, upload uses the existing queue panel
  const [showFolderConfirm, setShowFolderConfirm] = useState(false);
  const [folderFiles, setFolderFiles] = useState([]); // [{ file, relativePath, folderPath }]
  const [folderName, setFolderName] = useState("");
  const [folderStartDate, setFolderStartDate] = useState(null);
  const [folderEndDate, setFolderEndDate] = useState(null);
  const [folderSelectedChatIds, setFolderSelectedChatIds] = useState([]);
  const [folderTargetFolderId, setFolderTargetFolderId] = useState(undefined);
  // Shared links applied to every file of a folder upload.
  const [folderSourceUrls, setFolderSourceUrls] = useState([]);
  const folderHasCustomTimeRef = useRef(false);

  // Track if user has manually set a custom time (not the default 23:59)
  const hasCustomTimeRef = useRef(false);

  // Reset custom time flag when modal closes
  useEffect(() => {
    if (!showDateModal) {
      hasCustomTimeRef.current = false;
      setSourceUrls([]);
      setSameDatesForAll(true);
      setPerFileStartDates([]);
      setPerFileEndDates([]);
    }
  }, [showDateModal]);
  const isDateEnabledCollection =
    collectionName === "campaigns" ||
    collectionName === "general_information" ||
    collectionName === "internal_procedures";
  const isInternalProceduresCollection =
    collectionName === "internal_procedures";

  // uploadSourceUrls is index-aligned with files: one array of links per file.
  const handleMultipleFileUpload = useCallback(
    async (files, uploadStartDates = null, uploadEndDates = null, uploadSourceUrls = null) => {
      // Each entry keeps its index in `files`, because the per-file dates and links are aligned
      // with `files`: reading them by the filtered index shifts every file after a rejected one.
      const validFiles = [];
      const invalidFiles = [];

      // Validate all files first
      for (let sourceIndex = 0; sourceIndex < files.length; sourceIndex++) {
        const file = files[sourceIndex];
        const fileExtension = "." + file.name.split(".").pop().toLowerCase();
        const maxSize = 50 * 1024 * 1024; // 50MB

        if (!supportedTypes.includes(fileExtension)) {
          invalidFiles.push({
            name: file.name,
            reason: translate("fileTypeNotSupported", {
              extension: fileExtension,
            }),
          });
        } else if (file.size > maxSize) {
          invalidFiles.push({
            name: file.name,
            reason: translate("fileSizeTooLarge"),
          });
        } else {
          validFiles.push({ file, sourceIndex });
        }
      }

      // Show validation errors if any
      if (invalidFiles.length > 0) {
        toast.error(
          () => (
            <div style={{ fontSize: "13px" }}>
              <div style={{ fontWeight: 600, marginBottom: "6px" }}>
                {translate("someFilesCouldNotBeUploaded", { errors: "" }).replace(/:\s*$/, "")}
              </div>
              <ul style={{ margin: 0, paddingLeft: "16px", listStyle: "disc" }}>
                {invalidFiles.map((f, i) => (
                  <li key={i} style={{ marginBottom: "2px" }}>
                    <span style={{ fontWeight: 500 }}>{f.name}</span>{" "}
                    <span style={{ opacity: 0.7 }}>— {f.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          ),
          { duration: 8000 }
        );
      }

      if (validFiles.length === 0) return;

      // Initialize file queue with all valid files
      initFileQueue(validFiles.map((entry) => entry.file));

      // Close upload zone when upload starts
      if (onUploadStarted) onUploadStarted();

      setIsProcessingMultiple(validFiles.length > 1);
      setUploadingState(true);

      const results = [];
      const errors = [];

      for (let i = 0; i < validFiles.length; i++) {
        const { file, sourceIndex } = validFiles[i];

        // Mark current file as uploading
        updateFileInQueue(i, { status: "uploading" });

        try {
          const perFileStartISO = Array.isArray(uploadStartDates) ? uploadStartDates[sourceIndex] : null;
          const perFileEndISO = Array.isArray(uploadEndDates) ? uploadEndDates[sourceIndex] : null;
          const startDateISO = isDateEnabledCollection ? perFileStartISO : null;
          const endDateISO = isDateEnabledCollection ? perFileEndISO : null;

          const chatIdsToUse =
            selectedChatIds.length > 0 ? selectedChatIds : [];

          // Use override folder from drag-drop target, or current folder
          const effectiveFolderId = overrideFolderId !== undefined ? overrideFolderId : folderId;

          const perFileSourceUrls = Array.isArray(uploadSourceUrls) ? uploadSourceUrls[sourceIndex] : null;
          const result = await fileApi.uploadFile(
            file,
            null,
            collectionName,
            startDateISO,
            endDateISO,
            chatIdsToUse,
            effectiveFolderId,
            perFileSourceUrls
          );

          if (result.success) {
            results.push(result);
            updateFileInQueue(i, { status: "success" });
          } else {
            const errMsg = result.error || translate("uploadFailed");
            errors.push({ name: file.name, error: errMsg });
            updateFileInQueue(i, { status: "error", error: errMsg });
          }
        } catch (error) {
          let errMsg;
          const detail = error.response?.data?.detail;
          if (detail && typeof detail === "object" && detail.error === "pdf_page_limit_exceeded") {
            errMsg = translate("pdfPageLimitExceeded", {
              totalPages: detail.total_pages,
              maxPages: detail.max_pages,
            });
          } else {
            errMsg = (typeof detail === "string" ? detail : null) ||
              error.message ||
              translate("uploadFailed");
          }
          errors.push({ name: file.name, error: errMsg });
          updateFileInQueue(i, { status: "error", error: errMsg });
        }
      }

      // Refresh file list once after all uploads complete
      if (results.length > 0 && onFileUploaded) {
        onFileUploaded(results[results.length - 1]);
      }

      // Final summary
      setUploadingState(false);
      setIsProcessingMultiple(false);
      setOverrideFolderId(undefined); // Reset override after upload

      if (results.length > 0 && errors.length === 0) {
        toast.success(
          results.length === 1
            ? translate("allFilesUploadedSuccessfully")
            : translate("allFilesUploadedSuccessfullyPlural", { count: results.length })
        );
      } else if (results.length > 0 && errors.length > 0) {
        toast.error(
          translate("uploadComplete", {
            successful: results.length,
            failed: errors.length,
          }),
          { duration: 6000 }
        );
      } else if (errors.length > 0) {
        toast.error(
          translate("uploadFailedCount", {
            count: errors.length,
            plural: errors.length > 1 ? "s" : "",
          }),
          { duration: 6000 }
        );
      }
    },
    [
      supportedTypes,
      translate,
      onFileUploaded,
      collectionName,

      setIsProcessingMultiple,
      setUploadingState,
      isDateEnabledCollection,
      selectedChatIds,
      initFileQueue,
      updateFileInQueue,
      folderId,
      overrideFolderId,
      onUploadStarted,
    ]
  );

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(true);
    },
    [setIsDragOver]
  );

  const handleDragLeave = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);
    },
    [setIsDragOver]
  );

  const handleFilesSelected = useCallback(
    (files, targetFolderId) => {
      // Store override folder (undefined = use prop, null = root, string = specific folder)
      setOverrideFolderId(targetFolderId);

      const activeChatsCount = chats.filter((c) => c.is_active).length;

      // For internal_procedures, skip chat selection (auto-assigned to all internal chats)
      if (isInternalProceduresCollection && isDateEnabledCollection) {
        setPendingFiles(files);
        setSourceUrls(files.map(() => []));
        setPerFileStartDates(files.map(() => null));
        setPerFileEndDates(files.map(() => null));
        setStartDate(null);
        setEndDate(null);
        setShowDateModal(true);
      } else if (activeChatsCount > 1 && !isInternalProceduresCollection) {
        setPendingFiles(files);
        setSourceUrls(files.map(() => []));
        setPerFileStartDates(files.map(() => null));
        setPerFileEndDates(files.map(() => null));
        setStartDate(null);
        setEndDate(null);
        setShowDateModal(true);
      } else if (isDateEnabledCollection) {
        setPendingFiles(files);
        setSourceUrls(files.map(() => []));
        setPerFileStartDates(files.map(() => null));
        setPerFileEndDates(files.map(() => null));
        setStartDate(null);
        setEndDate(null);
        setShowDateModal(true);
      } else {
        handleMultipleFileUpload(files);
      }
    },
    [
      isDateEnabledCollection,
      isInternalProceduresCollection,
      handleMultipleFileUpload,
      chats,
    ]
  );

  // --- Folder upload flow ---
  const handleFolderEntries = useCallback(async (entries, targetFolderId) => {
    // entries: FileSystemEntry[] from webkitGetAsEntry or <input webkitdirectory>
    // Find the directory entry
    const dirEntry = entries.find((e) => e.isDirectory);
    if (!dirEntry) return;

    setFolderTargetFolderId(targetFolderId);
    setFolderName(dirEntry.name);

    // Show a loading toast while traversing
    const toastId = toast.loading(translate("loading"));
    try {
      const fileEntries = await readDirectoryEntriesRecursive(dirEntry);
      toast.dismiss(toastId);

      const validation = await validateFolderFiles(fileEntries, supportedTypes, translate);
      if (!validation.valid) {
        const errorList = validation.errors.map((e) => e.name ? `${e.name}: ${e.reason}` : e.reason).join("\n");
        toast.error(translate("invalidFilesInFolder") + "\n" + errorList, { duration: 8000 });
        return;
      }

      setFolderFiles(fileEntries);
      setFolderStartDate(null);
      setFolderEndDate(null);
      setFolderSelectedChatIds([]);
      folderHasCustomTimeRef.current = false;
      setShowFolderConfirm(true);
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err.message || translate("uploadFailed"));
    }
  }, [supportedTypes, translate]);

  // Handle <input webkitdirectory> change — convert File[] to entries
  const handleFolderInputChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    e.target.value = null;

    // webkitdirectory gives us flat File objects with webkitRelativePath
    // We need to reconstruct the tree from relative paths
    const rootFolderName = files[0].webkitRelativePath.split("/")[0];
    const fileEntries = files
      .filter((file) => !isIgnoredFile(file.name))
      .map((file) => {
        // webkitRelativePath = "FolderName/sub/file.txt" — strip the root folder name prefix
        const parts = file.webkitRelativePath.split("/");
        parts.shift(); // remove root folder name
        const relativePath = parts.join("/");
        const folderPath = parts.slice(0, -1).join("/");
        return { file, relativePath, folderPath };
      });

    setFolderTargetFolderId(undefined);
    setFolderName(rootFolderName);

    // Validate
    (async () => {
      const validation = await validateFolderFiles(fileEntries, supportedTypes, translate);
      if (!validation.valid) {
        const errorList = validation.errors.map((e) => e.name ? `${e.name}: ${e.reason}` : e.reason).join("\n");
        toast.error(translate("invalidFilesInFolder") + "\n" + errorList, { duration: 8000 });
        return;
      }
      setFolderFiles(fileEntries);
      setFolderStartDate(null);
      setFolderEndDate(null);
      setFolderSelectedChatIds([]);
      folderHasCustomTimeRef.current = false;
      setShowFolderConfirm(true);
    })();
  }, [supportedTypes, translate]);

  const handleFolderUploadConfirm = useCallback(async () => {
    // Validate chat selection
    const activeChatsCount = chats.filter((c) => c.is_active).length;
    if (activeChatsCount > 1 && folderSelectedChatIds.length === 0 && !isInternalProceduresCollection) {
      toast.error(translate("selectAtLeastOneChat"));
      return;
    }

    const folderLinksError = validateLinks(folderSourceUrls);
    if (folderLinksError) {
      toast.error(translate(folderLinksError, { max: MAX_SOURCE_LINKS }));
      return;
    }
    const cleanedFolderUrls = normalizeLinks(folderSourceUrls);

    // Close the confirmation modal
    setShowFolderConfirm(false);

    // Save dates and chats before clearing folder state
    const uploadStartDate = folderStartDate;
    const uploadEndDate = folderEndDate;
    const filesToUpload = [...folderFiles];
    const targetFolderIdToUse = folderTargetFolderId;

    // Store chat selection for the upload
    setSelectedChatIds(folderSelectedChatIds.length > 0 ? folderSelectedChatIds : []);

    // 1) Create root folder + subtree with a loading toast
    const toastId = toast.loading(translate("creatingFolders") || "Creating folders...");
    const effectiveParentFolderId = targetFolderIdToUse !== undefined ? targetFolderIdToUse : folderId;

    // Create the root folder (the OS folder itself)
    let rootFolderId = effectiveParentFolderId;
    try {
      const rootResult = await folderApi.createFolder(collectionName, folderName, effectiveParentFolderId);
      rootFolderId = rootResult.id;
    } catch {
      // If root folder creation fails, abort entirely
      toast.dismiss(toastId);
      toast.error(translate("failedToCreateFolder"));
      return;
    }

    // Create subfolders inside the root folder
    const tree = buildFolderTree(filesToUpload);
    const folderMap = await createFolderTree(tree, collectionName, rootFolderId);
    toast.dismiss(toastId);

    // 2) Assign each file its target folder_id and use overrideFolderId per file
    //    We'll upload sequentially using handleMultipleFileUpload-like logic,
    //    but we need per-file folder IDs, so we use the existing upload flow
    //    by uploading in groups per folder.

    // Group files by their resolved folder ID
    const filesByFolder = new Map();
    for (const entry of filesToUpload) {
      let resolvedFolderId = rootFolderId; // Files at folder root go into the created root folder
      if (entry.folderPath) {
        const mappedId = folderMap[entry.folderPath];
        if (mappedId === null) continue; // Folder creation failed — skip
        if (mappedId) resolvedFolderId = mappedId;
      }
      if (!filesByFolder.has(resolvedFolderId)) filesByFolder.set(resolvedFolderId, []);
      filesByFolder.get(resolvedFolderId).push(entry.file);
    }

    // Upload all files using the existing flow — set overrideFolderId for each group
    // Flatten all files into a single list and upload sequentially with per-file folder override
    const allFiles = [];
    const folderIdPerFile = [];
    for (const [resolvedFolderId, files] of filesByFolder) {
      for (const file of files) {
        allFiles.push(file);
        folderIdPerFile.push(resolvedFolderId);
      }
    }

    // Use the existing upload infrastructure (initFileQueue, updateFileInQueue, etc.)
    if (allFiles.length === 0) return;

    initFileQueue(allFiles);
    if (onUploadStarted) onUploadStarted();
    setIsProcessingMultiple(allFiles.length > 1);
    setUploadingState(true);

    const results = [];
    const errors = [];

    for (let i = 0; i < allFiles.length; i++) {
      const file = allFiles[i];
      updateFileInQueue(i, { status: "uploading" });

      try {
        const startDateISO = isDateEnabledCollection && uploadStartDate ? uploadStartDate.toISOString() : null;
        const endDateISO = isDateEnabledCollection && uploadEndDate ? uploadEndDate.toISOString() : null;
        const chatIdsToUse = folderSelectedChatIds.length > 0 ? folderSelectedChatIds : [];

        const result = await fileApi.uploadFile(
          file, null, collectionName, startDateISO, endDateISO, chatIdsToUse, folderIdPerFile[i], cleanedFolderUrls
        );

        if (result.success) {
          results.push(result);
          updateFileInQueue(i, { status: "success" });
        } else {
          const errMsg = result.error || translate("uploadFailed");
          errors.push({ name: file.name, error: errMsg });
          updateFileInQueue(i, { status: "error", error: errMsg });
        }
      } catch (error) {
        let errMsg;
        const detail = error.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.error === "pdf_page_limit_exceeded") {
          errMsg = translate("pdfPageLimitExceeded", { totalPages: detail.total_pages, maxPages: detail.max_pages });
        } else {
          errMsg = (typeof detail === "string" ? detail : null) || error.message || translate("uploadFailed");
        }
        errors.push({ name: file.name, error: errMsg });
        updateFileInQueue(i, { status: "error", error: errMsg });
      }
    }

    // Refresh file list
    if (results.length > 0 && onFileUploaded) {
      onFileUploaded(results[results.length - 1]);
    }

    // Final summary
    setUploadingState(false);
    setIsProcessingMultiple(false);

    if (results.length > 0 && errors.length === 0) {
      toast.success(
        results.length === 1
          ? translate("allFilesUploadedSuccessfully")
          : translate("allFilesUploadedSuccessfullyPlural", { count: results.length })
      );
    } else if (results.length > 0 && errors.length > 0) {
      toast.error(
        translate("uploadComplete", { successful: results.length, failed: errors.length }),
        { duration: 6000 }
      );
    } else if (errors.length > 0) {
      toast.error(
        translate("uploadFailedCount", { count: errors.length, plural: errors.length > 1 ? "s" : "" }),
        { duration: 6000 }
      );
    }

    // Reset folder state
    setFolderFiles([]);
    setFolderName("");
    setFolderStartDate(null);
    setFolderEndDate(null);
    setFolderSelectedChatIds([]);
    setFolderTargetFolderId(undefined);
    setFolderSourceUrls([]);
    folderHasCustomTimeRef.current = false;
  }, [
    folderFiles, folderName, folderTargetFolderId, folderId, collectionName,
    isDateEnabledCollection, folderStartDate, folderEndDate,
    folderSelectedChatIds, folderSourceUrls, isInternalProceduresCollection,
    chats, translate, onUploadStarted, onFileUploaded,
    initFileQueue, updateFileInQueue, setUploadingState,
    setIsProcessingMultiple, setSelectedChatIds,
  ]);

  const handleFolderUploadCancel = useCallback(() => {
    setShowFolderConfirm(false);
    setFolderFiles([]);
    setFolderName("");
    setFolderStartDate(null);
    setFolderEndDate(null);
    setFolderSelectedChatIds([]);
    setFolderTargetFolderId(undefined);
    setFolderSourceUrls([]);
    folderHasCustomTimeRef.current = false;
  }, []);

  const folderUploadModalRef = useModal(showFolderConfirm, handleFolderUploadCancel);

  useImperativeHandle(ref, () => ({
    handleFiles: (files, targetFolderId) => handleFilesSelected(files, targetFolderId),
    handleFolderUpload: (entries, targetFolderId) => handleFolderEntries(entries, targetFolderId),
    openFolderPicker: () => folderInputRef.current?.click(),
  }), [handleFilesSelected, handleFolderEntries]);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFilesSelected(files);
      }
    },
    [handleFilesSelected, setIsDragOver]
  );

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFilesSelected(files);
    }
    // Reset input
    e.target.value = null;
  };

  // One array of links per pending file; write a single slot without reading stale state.
  const setLinksAt = (index, links) =>
    setSourceUrls((prev) => {
      const updated = [...prev];
      updated[index] = links;
      return updated;
    });

  const handleConfirmUpload = () => {
    // Validate that at least one chat is selected when there are multiple active chats
    // Skip validation for internal_procedures (auto-assigned to all internal chats)
    const activeChatsCount = chats.filter((c) => c.is_active).length;
    if (
      activeChatsCount > 1 &&
      selectedChatIds.length === 0 &&
      !isInternalProceduresCollection
    ) {
      toast.error(translate("selectAtLeastOneChat"));
      return;
    }

    if (sameDatesForAll || pendingFiles.length === 1) {
      // Per-file link validation
      for (let i = 0; i < pendingFiles.length; i++) {
        const errorKey = validateLinks(sourceUrls[i]);
        if (errorKey) {
          const message = translate(errorKey, { max: MAX_SOURCE_LINKS });
          toast.error(`${message} (${pendingFiles[i].name})`);
          return;
        }
      }
      setShowDateModal(false);
      const startISOs = pendingFiles.map(() => (startDate ? startDate.toISOString() : null));
      const endISOs = pendingFiles.map(() => (endDate ? endDate.toISOString() : null));
      const cleanedUrls = pendingFiles.map((_, i) => normalizeLinks(sourceUrls[i]));
      handleMultipleFileUpload(pendingFiles, startISOs, endISOs, cleanedUrls);
    } else {
      // Per-file validation
      for (let i = 0; i < pendingFiles.length; i++) {
        const start = perFileStartDates[i];
        const end = perFileEndDates[i];
        if (start && end && start >= end) {
          toast.error(`${translate("startDateMustBeBeforeEndDate")} (${pendingFiles[i].name})`);
          return;
        }
        const errorKey = validateLinks(sourceUrls[i]);
        if (errorKey) {
          const message = translate(errorKey, { max: MAX_SOURCE_LINKS });
          toast.error(`${message} (${pendingFiles[i].name})`);
          return;
        }
      }
      setShowDateModal(false);
      const startISOs = perFileStartDates.map((d) => (d ? d.toISOString() : null));
      const endISOs = perFileEndDates.map((d) => (d ? d.toISOString() : null));
      const cleanedUrls = pendingFiles.map((_, i) => normalizeLinks(sourceUrls[i]));
      handleMultipleFileUpload(pendingFiles, startISOs, endISOs, cleanedUrls);
    }
    setPendingFiles([]);
    setSelectedChatIds([]); // Reset chat selection
  };

  const handleCancelUpload = () => {
    setShowDateModal(false);
    setPendingFiles([]);
    setStartDate(null);
    setEndDate(null);
    setSelectedChatIds([]);
    setOverrideFolderId(undefined);
  };

  const fileUploadDateModalRef = useModal(showDateModal, handleCancelUpload);

  return (
    <div>
      <div className="mb-4">
        <h3 className="font-semibold text-base mb-1" style={{ color: "var(--color-text-primary)" }}>
          {translate("uploadFiles")}
        </h3>
        <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
          {translate("uploadFilesDescription")} {supportedTypes.join(", ")}
        </p>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-10 text-center transition-all duration-200 ${
          uploading || isProcessingMultiple
            ? "opacity-50 pointer-events-none"
            : ""
        }`}
        style={{
          borderColor: isDragOver && !(uploading || isProcessingMultiple)
            ? "var(--color-mint)"
            : "var(--color-border)",
          backgroundColor: isDragOver && !(uploading || isProcessingMultiple)
            ? "var(--color-mint-muted)"
            : "var(--color-surface-raised)",
          ...(isDragOver && !(uploading || isProcessingMultiple)
            ? { transform: "scale(1.005)" }
            : {}),
        }}
        onDragOver={
          uploading || isProcessingMultiple ? undefined : handleDragOver
        }
        onDragLeave={
          uploading || isProcessingMultiple ? undefined : handleDragLeave
        }
        onDrop={uploading || isProcessingMultiple ? undefined : handleDrop}
      >
        <div
          className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
          style={{ backgroundColor: "var(--color-mint-muted)", border: "1px solid var(--color-mint-border)" }}
        >
          <Upload size={24} style={{ color: "var(--color-mint)" }} />
        </div>
        <div className="text-sm mb-3">
          {uploading || isProcessingMultiple ? (
            <div className="flex flex-col items-center">
              <div
                className="mb-3"
                style={{
                  width: 24,
                  height: 24,
                  border: "2px solid var(--color-mint)",
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              <p style={{ color: "var(--color-text-secondary)" }}>
                {translate("uploadingFiles") || "Uploading files..."}
              </p>
              <p className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                {translate("processingFiles") ||
                  "Please wait while we process your files..."}
              </p>
            </div>
          ) : (
            <>
              <p style={{ color: "var(--color-text-secondary)" }}>
                {translate("dragAndDrop")}
              </p>
              <label
                className="cursor-pointer font-semibold inline-block mt-1"
                style={{ color: "var(--color-mint)" }}
              >
                {translate("browseFiles")}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept={supportedTypes.join(",")}
                  onChange={handleFileSelect}
                  disabled={uploading || isProcessingMultiple}
                />
              </label>
              <p className="text-xs mt-1.5" style={{ color: "var(--color-text-muted)" }}>
                {translate("selectMultipleFiles")}
              </p>
            </>
          )}
        </div>
        <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
          {translate("supportedFormats")} {supportedTypes.join(", ")}
        </p>
      </div>

      {/* Campaign Date Modal — portal so it escapes any hidden parent */}
      {showDateModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={fileUploadDateModalRef}
            // Capped height with a scrollable body: the per-file links lists are unbounded,
            // so on a short viewport the panel would otherwise grow past it and take the
            // footer buttons with it.
            className="card rounded-xl border border-[var(--color-border)] p-6 max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col"
          >
            <div className="shrink-0 flex items-center mb-4">
              <Settings className="h-6 w-6 mr-2" style={{ color: "var(--color-mint)" }} />
              <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
                {isDateEnabledCollection
                  ? translate("fileSettings")
                  : translate("uploadFiles")}
              </h3>
            </div>

            {isDateEnabledCollection && (
              <p className="text-sm text-[var(--color-text-secondary)] mb-4">
                {sameDatesForAll || pendingFiles.length === 1
                  ? translate("campaignDatesDescription", {
                      count: pendingFiles.length,
                      plural: pendingFiles.length > 1 ? "s" : "",
                    })
                  : translate("perFileDescription")}
                {isInternalProceduresCollection && (
                  <span className="block mt-2" style={{ color: "var(--color-mint-dark)" }}>
                    Files will be automatically accessible to all internal
                    chats.
                  </span>
                )}
              </p>
            )}
            {isInternalProceduresCollection && !isDateEnabledCollection && (
              <p className="text-sm mb-4" style={{ color: "var(--color-mint-dark)" }}>
                Files will be automatically accessible to all internal chats.
              </p>
            )}

            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 mb-6 -mx-1 px-1">
              {isDateEnabledCollection && (
                <>
                  {pendingFiles.length > 1 && (
                    <div className="flex items-center gap-3 mb-4">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={sameDatesForAll}
                        onClick={() => {
                          const newValue = !sameDatesForAll;
                          if (!newValue) {
                            setPerFileStartDates(
                              perFileStartDates.map((d) => d ?? startDate)
                            );
                            setPerFileEndDates(
                              perFileEndDates.map((d) => d ?? endDate)
                            );
                          } else {
                            if (!startDate) {
                              const first = perFileStartDates.find((d) => d != null);
                              if (first) setStartDate(first);
                            }
                            if (!endDate) {
                              const first = perFileEndDates.find((d) => d != null);
                              if (first) setEndDate(first);
                            }
                          }
                          setSameDatesForAll(newValue);
                        }}
                        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                          sameDatesForAll
                            ? "bg-[var(--color-mint)]"
                            : "bg-[var(--color-border)]"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                            sameDatesForAll ? "translate-x-[18px]" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                      <span className="text-xs text-[var(--color-text-secondary)] select-none">
                        {translate("sameDatesForAllFiles")}
                      </span>
                    </div>
                  )}

                  {sameDatesForAll || pendingFiles.length === 1 ? (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                          {translate("from")}
                        </label>
                        <DatePicker
                          selected={startDate}
                          onChange={(date) => setStartDate(date)}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="dd/MM/yyyy HH:mm"
                          placeholderText={translate("selectStartDate")}
                          className="field-input w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                          {translate("until")}
                        </label>
                        <DatePicker
                          selected={endDate}
                          onChange={(date) => {
                            if (date) {
                              const adjustedDate = new Date(date);
                              const incomingTimeIsMidnight =
                                date.getHours() === 0 &&
                                date.getMinutes() === 0 &&
                                date.getSeconds() === 0;
                              const isFirstSelection = endDate === null;
                              const currentTimeIsDefault =
                                endDate &&
                                endDate.getHours() === 23 &&
                                endDate.getMinutes() === 59;
                              const currentTimeIsCustom =
                                endDate &&
                                !(
                                  endDate.getHours() === 23 &&
                                  endDate.getMinutes() === 59
                                );

                              // If user has custom time and incoming is midnight, preserve the custom time
                              if (
                                hasCustomTimeRef.current &&
                                incomingTimeIsMidnight
                              ) {
                                if (endDate) {
                                  adjustedDate.setHours(
                                    endDate.getHours(),
                                    endDate.getMinutes(),
                                    endDate.getSeconds(),
                                    endDate.getMilliseconds()
                                  );
                                }
                              } else if (
                                currentTimeIsCustom &&
                                incomingTimeIsMidnight
                              ) {
                                // Date change but user has custom time - preserve it
                                adjustedDate.setHours(
                                  endDate.getHours(),
                                  endDate.getMinutes(),
                                  endDate.getSeconds(),
                                  endDate.getMilliseconds()
                                );
                                hasCustomTimeRef.current = true;
                              } else if (!incomingTimeIsMidnight) {
                                // User is selecting a non-midnight time - mark as custom
                                hasCustomTimeRef.current = true;
                              } else if (
                                isFirstSelection ||
                                (incomingTimeIsMidnight && currentTimeIsDefault)
                              ) {
                                // First selection or date-only change - use default 23:59
                                adjustedDate.setHours(23, 59, 59, 999);
                                hasCustomTimeRef.current = false;
                              }
                              setEndDate(adjustedDate);
                            } else {
                              setEndDate(null);
                              hasCustomTimeRef.current = false;
                            }
                          }}
                          onTimeChange={(time) => {
                            if (time && endDate) {
                              const newDate = new Date(endDate);
                              newDate.setHours(
                                time.getHours(),
                                time.getMinutes(),
                                time.getSeconds(),
                                time.getMilliseconds()
                              );
                              hasCustomTimeRef.current = true;
                              setEndDate(newDate);
                            }
                          }}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="dd/MM/yyyy HH:mm"
                          placeholderText={translate("selectEndDate")}
                          className="field-input w-full"
                          minDate={startDate}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                          {translate("sourceUrlsLabel")}
                        </label>
                        {pendingFiles.length === 1 ? (
                          <SourceLinksField
                            value={sourceUrls[0] || []}
                            onChange={(links) => setLinksAt(0, links)}
                          />
                        ) : (
                          <div className="max-h-72 overflow-y-auto space-y-2 -mx-1 px-1">
                            {pendingFiles.map((file, i) => (
                              <div
                                key={i}
                                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-3 transition-shadow focus-within:shadow-[inset_2px_0_0_0_var(--color-mint)]"
                                style={{
                                  animation: `pageIn 220ms ease-out both`,
                                  animationDelay: `${i * 40}ms`,
                                }}
                              >
                                {/* The filename heads the card rather than sitting beside the
                                    fields: as a fixed 160px column it left the link row too
                                    narrow to hold a URL and a name side by side, so every row
                                    wrapped and the card read as broken. Stacked, the row gets
                                    the full card width and the filename stops being truncated. */}
                                <span
                                  className="block text-xs font-mono font-semibold text-[var(--color-text-primary)] truncate mb-2"
                                  title={file.name}
                                >
                                  {file.name}
                                </span>
                                <SourceLinksField
                                  value={sourceUrls[i] || []}
                                  onChange={(links) => setLinksAt(i, links)}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="max-h-96 overflow-y-auto space-y-2 -mx-1 px-1">
                      {pendingFiles.map((file, i) => (
                        <div
                          key={i}
                          className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-3 transition-shadow focus-within:shadow-[inset_2px_0_0_0_var(--color-mint)]"
                          style={{
                            animation: `pageIn 220ms ease-out both`,
                            animationDelay: `${i * 40}ms`,
                          }}
                        >
                          <span
                            className="text-xs font-mono font-semibold text-[var(--color-text-primary)] truncate"
                            title={file.name}
                          >
                            {file.name}
                          </span>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                                {translate("from")}
                              </label>
                              <DatePicker
                                selected={perFileStartDates[i] || null}
                                onChange={(date) => {
                                  const updated = [...perFileStartDates];
                                  updated[i] = date;
                                  setPerFileStartDates(updated);
                                }}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="dd/MM/yyyy HH:mm"
                                placeholderText={translate("selectStartDate")}
                                className="field-input w-full"
                                wrapperClassName="w-full"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                                {translate("until")}
                              </label>
                              <DatePicker
                                selected={perFileEndDates[i] || null}
                                onChange={(date) => {
                                  const updated = [...perFileEndDates];
                                  updated[i] = date;
                                  setPerFileEndDates(updated);
                                }}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="dd/MM/yyyy HH:mm"
                                placeholderText={translate("selectEndDate")}
                                className="field-input w-full"
                                wrapperClassName="w-full"
                                minDate={perFileStartDates[i] || null}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                              {translate("sourceUrlsLabel")}
                            </label>
                            <SourceLinksField
                              value={sourceUrls[i] || []}
                              onChange={(links) => setLinksAt(i, links)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Chat Selection for Multi-Chat Support - Always show if more than 1 active chat, but hide for internal_procedures (auto-assigned) */}
              {chats.filter((c) => c.is_active).length > 1 &&
                !isInternalProceduresCollection && (
                  <div>
                    <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                      {translate("selectChatsAccess")}{" "}
                      <span className="text-[var(--color-error)]">*</span>
                    </label>
                    <div className="max-h-40 overflow-y-auto border border-[var(--color-border)] rounded-xl p-3 space-y-2 bg-[var(--color-surface)]">
                      {chats
                        .filter((c) => c.is_active)
                        .sort(
                          (a, b) =>
                            (a.chatwoot_inbox_id || 0) -
                            (b.chatwoot_inbox_id || 0)
                        )
                        .map((chat) => (
                          <label
                            key={chat.id}
                            className="flex items-center space-x-2 cursor-pointer p-1 rounded-xl"
                          >
                            <input
                              type="checkbox"
                              value={chat.id}
                              checked={selectedChatIds.includes(chat.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedChatIds([
                                    ...selectedChatIds,
                                    chat.id,
                                  ]);
                                } else {
                                  setSelectedChatIds(
                                    selectedChatIds.filter(
                                      (id) => id !== chat.id
                                    )
                                  );
                                }
                              }}
                              className="h-4 w-4 border-[var(--color-border)] rounded"
                            />
                            <span className="text-sm text-[var(--color-text-secondary)]">
                              {chat.name}
                            </span>
                          </label>
                        ))}
                    </div>
                    <p className="text-xs text-[var(--color-error)] mt-1">
                      {translate("atLeastOneChatRequired")}
                    </p>
                  </div>
                )}
            </div>

            <div className="shrink-0 flex justify-end space-x-3">
              <button
                onClick={handleCancelUpload}
                className="btn-secondary"
              >
                {translate("cancel")}
              </button>
              <button
                onClick={handleConfirmUpload}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Upload className="h-4 w-4" />
                {translate("uploadFiles")}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Hidden folder input */}
      <input
        ref={folderInputRef}
        type="file"
        className="hidden"
        onChange={handleFolderInputChange}
        /* eslint-disable-next-line react/no-unknown-property */
        webkitdirectory=""
        directory=""
        multiple
      />

      {/* Folder Upload Confirmation Modal */}
      {showFolderConfirm && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={folderUploadModalRef}
            // The shared links list is unbounded, so cap the height and scroll. This modal's
            // sections are siblings with no single body wrapper, so the whole panel scrolls
            // (footer included) rather than just a body region, as in the other two modals.
            className="card rounded-xl border border-[var(--color-border)] p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <FolderUp className="h-6 w-6 mr-2" style={{ color: "var(--color-mint)" }} />
                <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
                  {translate("uploadFolder")}
                </h3>
              </div>
              <button
                onClick={handleFolderUploadCancel}
                className="p-1 rounded-lg transition-colors"
                style={{ color: "var(--color-text-muted)" }}
              >
                <X size={18} />
              </button>
            </div>

            <div className="text-sm text-[var(--color-text-secondary)] mb-4 space-y-2">
              <p><strong>{folderName}</strong></p>
              <p>
                {translate("uploadFolderSummary", {
                  files: folderFiles.length,
                  folders: Object.keys(buildFolderTree(folderFiles)).length,
                  pdf: folderFiles.filter(f => f.file.name.toLowerCase().endsWith(".pdf")).length,
                  txt: folderFiles.filter(f => f.file.name.toLowerCase().endsWith(".txt")).length,
                  md: folderFiles.filter(f => f.file.name.toLowerCase().endsWith(".md")).length,
                })}
              </p>
            </div>

            {/* Date picker for date-enabled collections */}
            {isDateEnabledCollection && (
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                    {translate("startDate")}
                  </label>
                  <DatePicker
                    selected={folderStartDate}
                    onChange={(date) => setFolderStartDate(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm"
                    placeholderText={translate("selectStartDate")}
                    className="field-input w-full"
                  />
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    {translate("startDateOptional")}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                    {translate("endDate")}
                  </label>
                  <DatePicker
                    selected={folderEndDate}
                    onChange={(date) => {
                      if (date) {
                        const adjusted = new Date(date);
                        const isMidnight = date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;
                        const isFirst = folderEndDate === null;
                        if (folderHasCustomTimeRef.current && isMidnight && folderEndDate) {
                          adjusted.setHours(folderEndDate.getHours(), folderEndDate.getMinutes(), folderEndDate.getSeconds(), folderEndDate.getMilliseconds());
                        } else if (!isMidnight) {
                          folderHasCustomTimeRef.current = true;
                        } else if (isFirst || isMidnight) {
                          adjusted.setHours(23, 59, 59, 999);
                          folderHasCustomTimeRef.current = false;
                        }
                        setFolderEndDate(adjusted);
                      } else {
                        setFolderEndDate(null);
                        folderHasCustomTimeRef.current = false;
                      }
                    }}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm"
                    placeholderText={translate("selectEndDate")}
                    className="field-input w-full"
                    minDate={folderStartDate}
                  />
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    {translate("noEndDate")}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                    {translate("sourceUrlsLabel")}
                  </label>
                  <SourceLinksField
                    value={folderSourceUrls}
                    onChange={setFolderSourceUrls}
                  />
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    {translate("sourceUrlsHelper")}
                  </p>
                </div>
              </div>
            )}

            {/* Chat selection */}
            {chats.filter((c) => c.is_active).length > 1 && !isInternalProceduresCollection && (
              <div className="mb-4">
                <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                  {translate("selectChatsAccess")}{" "}
                  <span className="text-[var(--color-error)]">*</span>
                </label>
                <div className="max-h-40 overflow-y-auto border border-[var(--color-border)] rounded-xl p-3 space-y-2 bg-[var(--color-surface)]">
                  {chats
                    .filter((c) => c.is_active)
                    .sort((a, b) => (a.chatwoot_inbox_id || 0) - (b.chatwoot_inbox_id || 0))
                    .map((chat) => (
                      <label key={chat.id} className="flex items-center space-x-2 cursor-pointer p-1 rounded-xl">
                        <input
                          type="checkbox"
                          value={chat.id}
                          checked={folderSelectedChatIds.includes(chat.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFolderSelectedChatIds([...folderSelectedChatIds, chat.id]);
                            } else {
                              setFolderSelectedChatIds(folderSelectedChatIds.filter((id) => id !== chat.id));
                            }
                          }}
                          className="h-4 w-4 border-[var(--color-border)] rounded"
                        />
                        <span className="text-sm text-[var(--color-text-secondary)]">{chat.name}</span>
                      </label>
                    ))}
                </div>
                <p className="text-xs text-[var(--color-error)] mt-1">
                  {translate("atLeastOneChatRequired")}
                </p>
              </div>
            )}

            {isInternalProceduresCollection && (
              <p className="text-sm mb-4" style={{ color: "var(--color-mint-dark)" }}>
                Files will be automatically accessible to all internal chats.
              </p>
            )}

            <div className="flex justify-end space-x-3">
              <button onClick={handleFolderUploadCancel} className="btn-secondary">
                {translate("cancel")}
              </button>
              <button onClick={handleFolderUploadConfirm} className="btn-primary">
                {translate("uploadFiles")}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
});

export default FileUpload;
